"use client";

import React, { useEffect, useRef, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NotificationItem } from "./NotificationItem";
import { useNotifications } from "@/hooks/useNotifications";
import { Loader2, BellOff, CheckCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

interface NotificationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  open,
  onOpenChange,
}) => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    refreshNotifications,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Fetch notifications when panel opens
  useEffect(() => {
    if (open && (!notifications || notifications.length === 0) && !loadingRef.current) {
      loadingRef.current = true;
      refreshNotifications();
      setTimeout(() => {
        loadingRef.current = false;
      }, 1000);
    }
  }, [open, notifications?.length, refreshNotifications]);

  // Handle scroll to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const bottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

      if (bottom && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore]
  );

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    toast.success("All notifications marked as read");
  };

  const handleRefresh = () => {
    refreshNotifications();
    toast.success("Notifications refreshed");
  };

  const handleDelete = (notificationId: string) => {
    deleteNotification(notificationId);
    toast.success("Notification deleted");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SheetTitle>Notifications</SheetTitle>
              {unreadCount > 0 && (
                <Badge variant="default" className="bg-primary">
                  {unreadCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
                className="h-8"
                title="Refresh notifications"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="h-8"
                  title="Mark all as read"
                >
                  <CheckCheck className="h-4 w-4 mr-1" />
                  Mark all
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {error && (
            <div className="p-6 text-center">
              <p className="text-sm text-destructive">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="mt-4"
              >
                Try Again
              </Button>
            </div>
          )}

          {!error && loading && (!notifications || notifications.length === 0) && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground mt-2">
                  Loading notifications...
                </p>
              </div>
            </div>
          )}

          {!error && !loading && (!notifications || notifications.length === 0) && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center px-6">
                <div className="h-16 w-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                  <BellOff className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="font-medium text-lg mb-2">No notifications</h3>
                <p className="text-sm text-muted-foreground">
                  You're all caught up! Check back later for updates.
                </p>
              </div>
            </div>
          )}

          {!error && notifications && notifications.length > 0 && (
            <ScrollArea
              className="h-full"
              onScrollCapture={handleScroll}
              ref={scrollAreaRef}
            >
              <div>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onDelete={handleDelete}
                  />
                ))}

                {/* Loading more indicator */}
                {loading && notifications && notifications.length > 0 && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                )}

                {/* End of list */}
                {!hasMore && notifications && notifications.length > 0 && (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    No more notifications
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
